import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { reportListApi } from "@/services/report-list-api"
import { queryKeys } from "./query-keys"
import { useAuthReady } from "./use-auth-ready"
import { useCurrentTenant, getCachedTenantId } from "./use-current-tenant"
import type {
  ReportList,
  ReportListCreateRequest,
  ReportListUpdateRequest,
} from "@/types/report-list"

export function useReportLists() {
  const isAuthReady = useAuthReady()
  const { data: tenant } = useCurrentTenant()

  return useQuery({
    queryKey: queryKeys.reportLists.list(tenant?.id),
    queryFn: () => reportListApi.getAllReportLists(),
    enabled: isAuthReady && tenant !== undefined,
    throwOnError: true,
  })
}

export function useReportList(reportListKey: string) {
  const isAuthReady = useAuthReady()

  return useQuery({
    queryKey: queryKeys.reportLists.detail(reportListKey),
    queryFn: () => reportListApi.getReportListByKey(reportListKey),
    enabled: isAuthReady && !!reportListKey,
  })
}

export function useCreateReportList() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ReportListCreateRequest) =>
      reportListApi.createReportList(request),
    onSuccess: () => {
      const tenantId = getCachedTenantId(queryClient)
      return queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) })
    },
  })
}

export function useUpdateReportList() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ReportListUpdateRequest) =>
      reportListApi.updateReportList(request),
    onMutate: async (request) => {
      const listKey = request.reportList.reportListKey
      const tenantId = getCachedTenantId(queryClient)

      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.reportLists.list(tenantId) })

      // Snapshot previous values for rollback
      const previousLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      )
      const previousDetail = queryClient.getQueryData<ReportList>(
        queryKeys.reportLists.detail(listKey),
      )

      // Optimistically update the list cache
      queryClient.setQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
        (old) =>
          old?.map((list) =>
            list.reportListKey === listKey
              ? { ...list, name: request.reportList.name }
              : list,
          ),
      )

      // Optimistically update the detail cache
      queryClient.setQueryData<ReportList>(
        queryKeys.reportLists.detail(listKey),
        (old) => (old ? { ...old, name: request.reportList.name } : old),
      )

      return { previousLists, previousDetail, listKey, tenantId }
    },
    onError: (_err, _request, context) => {
      // Rollback to previous values on error
      if (context?.previousLists) {
        queryClient.setQueryData(
          queryKeys.reportLists.list(context.tenantId),
          context.previousLists,
        )
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.reportLists.detail(context.listKey),
          context.previousDetail,
        )
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      const tenantId = getCachedTenantId(queryClient)
      return queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) })
    },
  })
}

export function useDeleteReportList() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reportListKey: string) =>
      reportListApi.deleteReportList(reportListKey),
    onMutate: async (reportListKey) => {
      const tenantId = getCachedTenantId(queryClient)
      await queryClient.cancelQueries({ queryKey: queryKeys.reportLists.list(tenantId) })

      const previousLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      )

      // Optimistically remove the list from cache
      queryClient.setQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
        (old) => old?.filter((list) => list.reportListKey !== reportListKey),
      )

      // Remove the detail cache entry
      queryClient.removeQueries({
        queryKey: queryKeys.reportLists.detail(reportListKey),
      })

      return { previousLists, reportListKey, tenantId }
    },
    onError: (_err, _reportListKey, context) => {
      if (context?.previousLists) {
        queryClient.setQueryData(
          queryKeys.reportLists.list(context.tenantId),
          context.previousLists,
        )
      }
    },
    onSettled: () => {
      const tenantId = getCachedTenantId(queryClient)
      return queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) })
    },
  })
}
